type LogLevel = 'debug' | 'info' | 'warn' | 'error';

/**
 * Frontend logger with environment-aware logging
 * In development: logs all levels
 * In production: only logs warn and error, and sends errors to Sentry
 */
class Logger {
    private isDevelopment = import.meta.env.MODE === 'development';

    private log(level: LogLevel, message: string, meta?: any): void {
        // Skip debug logs in production
        if (!this.isDevelopment && level === 'debug') {
            return;
        }

        const timestamp = new Date().toISOString();
        const logMessage = `[${timestamp}] [${level.toUpperCase()}] ${message}`;

        switch (level) {
            case 'debug':
                console.debug(logMessage, meta || '');
                break;
            case 'info':
                console.info(logMessage, meta || '');
                break;
            case 'warn':
                console.warn(logMessage, meta || '');
                break;
            case 'error':
                console.error(logMessage, meta || '');
                // Send to error tracking in production (Sentry will be added)
                if (!this.isDevelopment && (window as any).Sentry) {
                    (window as any).Sentry.captureException(new Error(message), { extra: meta });
                }
                break;
        }
    }

    debug(message: string, meta?: any): void {
        this.log('debug', message, meta);
    }

    info(message: string, meta?: any): void {
        this.log('info', message, meta);
    }

    warn(message: string, meta?: any): void {
        this.log('warn', message, meta);
    }

    error(message: string, meta?: any): void {
        this.log('error', message, meta);
    }
}

export const logger = new Logger();
export default logger;
